import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Plus, Building2, MapPin, Edit2 } from "lucide-react";
import { Sidebar } from "@/components/Navigation";
import BottomNav from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const StudioManagement = ({ user, onLogout }) => {
  const [studios, setStudios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingStudio, setEditingStudio] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    location: "",
    rooms: [],
    is_active: true,
  });
  const [roomName, setRoomName] = useState("");
  const [roomCapacity, setRoomCapacity] = useState("");
  const [editingRoomIndex, setEditingRoomIndex] = useState(null);

  useEffect(() => {
    fetchStudios();
  }, []);

  const fetchStudios = async () => {
    try {
      // Fetch all studios including inactive ones
      const response = await api.get("/studios?include_inactive=true");
      setStudios(response.data.studios);
    } catch (error) {
      toast.error("Failed to load studios");
    } finally {
      setLoading(false);
    }
  };

  const toggleStudioStatus = async (studio) => {
    try {
      const updatedStudio = {
        ...studio,
        is_active: !studio.is_active
      };
      await api.put(`/studios/${studio.studio_id}`, updatedStudio);
      toast.success(studio.is_active ? "Studio deactivated" : "Studio activated");
      fetchStudios();
    } catch (error) {
      toast.error("Failed to update studio status");
    }
  };

  const addRoom = () => {
    if (!roomName || !roomCapacity) {
      toast.error("Please enter room name and capacity");
      return;
    }

    if (editingRoomIndex !== null) {
      // Update existing room
      const updatedRooms = [...formData.rooms];
      updatedRooms[editingRoomIndex] = {
        ...updatedRooms[editingRoomIndex],
        name: roomName,
        capacity: parseInt(roomCapacity),
      };
      setFormData({ ...formData, rooms: updatedRooms });
      setEditingRoomIndex(null);
      toast.success("Room updated");
    } else {
      // Add new room
      const newRoom = {
        room_id: `room_${Date.now()}`,
        name: roomName,
        capacity: parseInt(roomCapacity),
        is_active: true,
      };
      setFormData({ ...formData, rooms: [...formData.rooms, newRoom] });
      toast.success("Room added");
    }
    
    setRoomName("");
    setRoomCapacity("");
  };

  const toggleRoomStatus = (index) => {
    const updatedRooms = [...formData.rooms];
    updatedRooms[index].is_active = !updatedRooms[index].is_active;
    setFormData({ ...formData, rooms: updatedRooms });
  };

  const editRoom = (index) => {
    const room = formData.rooms[index];
    setRoomName(room.name);
    setRoomCapacity(room.capacity.toString());
    setEditingRoomIndex(index);
  };

  const createStudio = async () => {
    if (!formData.name || !formData.location) {
      toast.error("Please fill all fields");
      return;
    }

    try {
      if (editMode && editingStudio) {
        await api.put(`/studios/${editingStudio.studio_id}`, formData);
        toast.success("Studio updated successfully!");
      } else {
        await api.post("/studios", formData);
        toast.success("Studio created successfully!");
      }
      
      fetchStudios();
      setOpen(false);
      setEditMode(false);
      setEditingStudio(null);
      setFormData({ name: "", location: "", rooms: [] });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save studio");
    }
  };

  const startEditStudio = (studio) => {
    setEditMode(true);
    setEditingStudio(studio);
    // Preserve ALL fields from the existing studio doc — the original version
    // of this function only copied {name, location, rooms, is_active}, so when
    // the admin saved (even without changes) the destructive PUT wiped address,
    // contact_number, working_hours, branding, etc. Spreading the full studio
    // keeps those values intact even though this dialog doesn't expose them.
    setFormData({
      ...studio,
      name: studio.name,
      location: studio.location,
      rooms: studio.rooms || [],
      is_active: studio.is_active !== undefined ? studio.is_active : true,
    });
    setOpen(true);
  };

  return (
    <div className="min-h-screen bg-slate-50 md:pl-64">
      <Sidebar user={user} onLogout={onLogout} />
      
      <div className="p-4 md:p-8 pb-20 md:pb-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-4xl font-bold font-heading text-slate-900" data-testid="studio-management-title">
              Studio Management
            </h1>
            <p className="text-sm text-slate-600 mt-1">Manage your studio locations and rooms</p>
          </div>
          <Dialog open={open} onOpenChange={(isOpen) => {
            setOpen(isOpen);
            if (!isOpen) {
              setEditMode(false);
              setEditingStudio(null);
              setFormData({ name: "", location: "", rooms: [], is_active: true });
              setRoomName("");
              setRoomCapacity("");
              setEditingRoomIndex(null);
            }
          }}>
            <DialogTrigger asChild>
              <Button data-testid="add-studio-button" className="h-12 rounded-full bg-primary hover:bg-primary/90 font-medium">
                <Plus className="w-5 h-5 mr-2" />
                Add Studio
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md max-h-[90vh]">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">
                  {editMode ? "Edit Studio" : "Create New Studio"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-6 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="studio-name">Studio Name</Label>
                  <Input
                    id="studio-name"
                    data-testid="studio-name-input"
                    placeholder="Enter studio name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="h-12 rounded-xl"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="studio-location">Location</Label>
                  <Input
                    id="studio-location"
                    data-testid="studio-location-input"
                    placeholder="Enter location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="h-12 rounded-xl"
                  />
                </div>

                {editMode && (
                  <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                    <input
                      type="checkbox"
                      id="studio-active"
                      data-testid="studio-active-checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="studio-active" className="cursor-pointer">
                      Studio is Active
                      {!formData.is_active && <span className="text-red-600 ml-2">(Inactive studios won't appear in selections)</span>}
                    </Label>
                  </div>
                )}

                <div className="border-t pt-4">
                  <Label className="text-lg font-heading">Add Rooms</Label>
                  <div className="space-y-3 mt-3">
                    <div className="flex gap-2">
                      <Input
                        data-testid="room-name-input"
                        placeholder="Room name"
                        value={roomName}
                        onChange={(e) => setRoomName(e.target.value)}
                        className="h-10 rounded-lg flex-1"
                      />
                      <Input
                        data-testid="room-capacity-input"
                        type="number"
                        placeholder="Capacity"
                        value={roomCapacity}
                        onChange={(e) => setRoomCapacity(e.target.value)}
                        className="h-10 rounded-lg w-24"
                      />
                      <Button data-testid="add-room-button" onClick={addRoom} size="sm" className="h-10 rounded-lg">
                        {editingRoomIndex !== null ? "Update" : <Plus className="w-4 h-4" />}
                      </Button>
                    </div>

                    {formData.rooms.length > 0 && (
                      <div className="space-y-2">
                        {formData.rooms.map((room, index) => (
                          <div
                            key={room.room_id}
                            data-testid={`room-item-${index}`}
                            className={`flex items-center justify-between p-3 rounded-lg text-sm ${
                              room.is_active ? 'bg-slate-50' : 'bg-red-50'
                            }`}
                          >
                            <div className="flex-1">
                              <span className="font-medium">{room.name}</span>
                              <span className="text-slate-600 ml-3">Capacity: {room.capacity}</span>
                              {!room.is_active && <span className="text-red-600 ml-2">(Inactive)</span>}
                            </div>
                            <div className="flex gap-2">
                              <Button
                                data-testid={`edit-room-${index}`}
                                onClick={() => editRoom(index)}
                                size="sm"
                                variant="outline"
                                className="h-8 px-2"
                              >
                                Edit
                              </Button>
                              <Button
                                data-testid={`toggle-room-${index}`}
                                onClick={() => toggleRoomStatus(index)}
                                size="sm"
                                variant={room.is_active ? "destructive" : "default"}
                                className="h-8 px-2"
                              >
                                {room.is_active ? "Deactivate" : "Activate"}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <Button
                  data-testid="create-studio-button"
                  onClick={createStudio}
                  className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 font-medium"
                >
                  {editMode ? "Update Studio" : "Create Studio"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : studios.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-slate-100">
            <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">No studios yet. Create your first studio!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {studios.map((studio, index) => (
              <motion.div
                key={studio.studio_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`bg-white rounded-2xl p-6 shadow-sm border hover:shadow-lg transition-all ${
                  studio.is_active === false ? 'border-red-200 bg-red-50/30' : 'border-slate-100'
                }`}
                data-testid={`studio-card-${index}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-xl ${
                    studio.is_active === false 
                      ? 'bg-gradient-to-br from-red-400 to-red-500' 
                      : 'bg-gradient-to-br from-violet-500 to-purple-500'
                  }`}>
                    <Building2 className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-xl font-bold font-heading text-slate-900">{studio.name}</h3>
                      {studio.is_active === false && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-600 rounded-full text-xs font-medium">
                          Inactive
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 flex items-center gap-1 mt-1">
                      <MapPin className="w-4 h-4" />
                      {studio.location}
                    </p>
                  </div>
                </div>

                {studio.rooms.length > 0 && (
                  <div className="mt-6 space-y-2">
                    <p className="text-sm font-medium text-slate-900">Rooms:</p>
                    {studio.rooms.map((room) => (
                      <div
                        key={room.room_id}
                        className={`flex items-center justify-between p-3 rounded-lg ${
                          room.is_active === false ? 'bg-red-50' : 'bg-slate-50'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{room.name}</span>
                          {room.is_active === false && (
                            <span className="px-1.5 py-0.5 bg-red-100 text-red-600 rounded text-xs">Inactive</span>
                          )}
                        </div>
                        <span className="text-xs text-slate-600">Capacity: {room.capacity}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-4 flex gap-2">
                  <Button
                    data-testid={`edit-studio-${index}`}
                    onClick={() => startEditStudio(studio)}
                    className="flex-1 h-10 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700"
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit Studio
                  </Button>
                  {studio.is_active === false && (
                    <Button
                      data-testid={`activate-studio-${index}`}
                      onClick={() => toggleStudioStatus(studio)}
                      className="h-10 px-4 rounded-full bg-green-500 hover:bg-green-600 text-white"
                    >
                      Activate
                    </Button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <BottomNav user={user} onLogout={onLogout} />
    </div>
  );
};

export default StudioManagement;